import {
  Modal,
  Typography,
  Button,
  MarkedList,
  FormAlertLabel,
  LazyImage,
} from '@frontend/design_system'
import { PROFILE_DETAILS_UPLOAD_LOGO_MODAL } from 'texts/profileDetails'
import { CANCEL } from 'texts/uiTexts'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { PlusIcon } from 'ui/icons'
import styles from './styles.module.scss'
import { UploadLogoModalProps } from './types'
import { ADD } from 'texts/common'
import { useRef, useState, useCallback } from 'react'
import { useFileValidation } from 'hooks/useFileValidation'
import { MAX_FILES_SIZE_LOGO } from 'constants/files'
import { getBase64, dataURLtoFile } from 'ui/atoms/UploadFile/utils'
import { joinClasses } from 'utils/joinClasses'
import { formats } from 'ui/components/ProfileDetailsForm/constants'
import minusIcon from 'assets/icons/blackMaster/blackMaster_minus_24x24.webp'
import plusIcon from 'assets/icons/blackMaster/blackMaster_plus_24x24.webp'
import { MAX_ZOOM, MIN_ZOOM, STEP_ZOOM } from './constants'
import { CustomFile } from 'ui/atoms/UploadFile/types'

export const UploadLogoModal = ({
  handleUpdateLogo,
  onClose,
}: UploadLogoModalProps) => {
  const cropperRef = useRef<HTMLImageElement | ReactCropperElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [image, setImage] = useState('')
  const [zoom, setZoom] = useState(1)
  const [isDragOver, setIsDragOver] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { validateFiles } = useFileValidation()

  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom)
    const cropper = (cropperRef.current as ReactCropperElement)?.cropper
    if (cropper) {
      cropper.zoomTo(newZoom)
    }
  }

  const handleFileSelect = useCallback(
    async (file: File) => {
      setErrorMessage('')
      if (!file || !file.type.startsWith('image/')) {
        return
      }

      const validation = await validateFiles(file, {
        formats,
        maxSize: MAX_FILES_SIZE_LOGO,
        checkDimensions: false,
      })

      if (!validation.isValid) {
        if (validation.errorMessage) setErrorMessage(validation.errorMessage)
        return
      }

      const base64 = await getBase64(file)
      setImage(base64)
    },
    [validateFiles]
  )

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const files = e.target.files
    if (files && files[0]) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      const files = e.dataTransfer.files
      if (files && files[0]) {
        handleFileSelect(files[0])
      }
    },
    [handleFileSelect]
  )

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click()
  }

  const setDefaultCropSize = () => {
    const cropper = cropperRef.current as ReactCropperElement
    if (cropper.cropper) {
      cropper.cropper.setCropBoxData({
        left: 100,
        top: 50,
        width: 100,
        height: 40,
      })
    }
  }

  const getCropData = () => {
    const cropper = cropperRef.current as ReactCropperElement
    if (cropper?.cropper) {
      const dataURL = cropper.cropper.getCroppedCanvas().toDataURL()
      const file: CustomFile = dataURLtoFile(dataURL, 'cropped-logo.png')
      file.url = dataURL
      return file
    }
    return null
  }

  const handleSave = () => {
    const cropData = getCropData()
    if (cropData) {
      handleUpdateLogo([cropData])
    }
    onClose()
  }
  return (
    <Modal
      position="center-horizontal"
      title={PROFILE_DETAILS_UPLOAD_LOGO_MODAL.TITLE}
      headerSize="large"
      isDelimiter
    >
      <div className={styles.content}>
        <Typography name="body1WMedium" color="grey500">
          {PROFILE_DETAILS_UPLOAD_LOGO_MODAL.CONTENT}
          <MarkedList
            typographyNameForHtml="body1WMedium"
            color="grey500"
            html={PROFILE_DETAILS_UPLOAD_LOGO_MODAL.RULES}
          />
        </Typography>
        {!image ? (
          <div
            className={joinClasses(styles['upload-area'], [
              styles['drag-over'],
              isDragOver,
            ])}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadAreaClick}
          >
            <div className={styles['upload-content']}>
              <PlusIcon size="large" color="blue700" />
              <Typography
                Tag="div"
                name="caption1WMedium"
                color="blue700"
                className={styles['upload-content-title']}
              >
                {PROFILE_DETAILS_UPLOAD_LOGO_MODAL.CLICK_OR_DRAG_AND_DROP}
                <Typography name="caption1WMedium" color="blue700">
                  {PROFILE_DETAILS_UPLOAD_LOGO_MODAL.TO_UPLOAD_YOUR_LOGO}
                </Typography>
              </Typography>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={formats.map((format) => `.${format}`).join(', ')}
              onChange={onChange}
              className={styles['upload-input']}
            />
          </div>
        ) : (
          <div className={styles.cropper}>
            <Cropper
              ref={cropperRef}
              ready={setDefaultCropSize}
              style={{ width: '100%' }}
              src={image}
              zoomTo={0.5}
              cropBoxResizable={false}
              zoomOnWheel={false}
              zoomOnTouch={false}
              dragMode="move"
              cropBoxMovable={false}
              viewMode={1}
              background={false}
              autoCropArea={1}
            />
          </div>
        )}
        {errorMessage && (
          <FormAlertLabel
            label={errorMessage}
            type="error"
            position="static"
            typographyName="body2WMedium"
          />
        )}
        {image && (
          <div className={styles['zoom-container']}>
            <LazyImage
              src={minusIcon}
              width={24}
              height={24}
              onClick={() =>
                handleZoomChange(Math.max(MIN_ZOOM, zoom - STEP_ZOOM))
              }
            />
            <input
              type="range"
              min={0}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => handleZoomChange(Number(e.target.value))}
              className={styles['zoom-input']}
            />
            <LazyImage
              src={plusIcon}
              width={24}
              height={24}
              onClick={() =>
                handleZoomChange(Math.min(MAX_ZOOM, zoom + STEP_ZOOM))
              }
            />
          </div>
        )}
        <div className={styles.buttons}>
          <Button
            label={CANCEL}
            size="large"
            variant="secondary-border"
            onClick={onClose}
          />
          <Button
            label={ADD}
            size="large"
            onClick={handleSave}
            disabled={!image}
          />
        </div>
      </div>
    </Modal>
  )
}
